import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { CreateConsultationDto } from '@/core/models/consultation.model'
import { MeetingLinkOrder } from '@/core/models/meeting-link-order.model'
import { OrderStatus } from '@/core/models/order.model'
import { useCreateConsultationMutation } from '@/core/services/consultation'
import { useUpdateMeetingLinkOrderMutation } from '@/core/services/order'
import { getApiErrorMessage } from '@/utils/errors'

type UseCreateConsultationFromOrderOptions = {
  order: MeetingLinkOrder
  onSuccess?: () => void
  navigateTo?: (consultationId: string) => string
}

export function useCreateConsultationFromOrder({ order, onSuccess, navigateTo }: UseCreateConsultationFromOrderOptions) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [createConsultation] = useCreateConsultationMutation()
  const [updateOrder] = useUpdateMeetingLinkOrderMutation()
  const [isLoading, setIsLoading] = useState(false)
  const executingRef = useRef(false)

  const execute = async (consultationParams: CreateConsultationDto) => {
    // Guard: if the order already has a consultation, navigate to it instead of creating a duplicate
    if (order.consultation?._id) {
      const path = navigateTo ? navigateTo(order.consultation._id) : `/consultations/${order.consultation._id}/local`
      navigate(path)
      onSuccess?.()
      return
    }

    // Prevent double-click / concurrent execution
    if (executingRef.current) return
    executingRef.current = true

    setIsLoading(true)
    try {
      const consultation = await createConsultation(consultationParams).unwrap()

      await updateOrder({
        id: order._id,
        consultation: consultation._id,
        status: OrderStatus.Consultation_Created,
      }).unwrap()

      const path = navigateTo ? navigateTo(consultation._id) : `/consultations/${consultation._id}/local`
      navigate(path)
      onSuccess?.()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.startError')), { variant: 'error' })
    } finally {
      setIsLoading(false)
      executingRef.current = false
    }
  }

  return { execute, isLoading }
}
